import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { formatCurrency } from './CoreFunctions';
import AggregatedAssetExpenseChart from './AggregatedAssetExpenseChart';
import RecurringManager from './RecurringManager.jsx';
import ChargingEnergyChart from './ChargingEnergyChart';
import Plot from 'react-plotly.js';
import Modal from './components/Modal';
import ValuationForm from './ValuationForm';
import ValuationsModal from './components/ValuationsModal';
import AssetExpenseLinkModal from './components/AssetExpenseLinkModal.jsx';
import PhysicalAssetToDoList from './components/PhysicalAssetToDoList.jsx';
import { buildAssetLabel, getDomainForCategory } from './utils/assetLabelHelper';
import { buildCategoryTreeSorted } from './utils/expenseCategories';
import { exportToExcel } from './utils/exportUtils';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Configure axios to always send cookies
axios.defaults.withCredentials = true;

const ExpenseGroupPill = ({ group }) => {
  if (!group) return null;
  const colors = {
    'Operating': { bg: '#3B82F6', text: '#FFFFFF' },
    'Financing': { bg: '#F59E0B', text: '#FFFFFF' },
    'CapEx': { bg: '#10B981', text: '#FFFFFF' },
  };
  const style = colors[group] || { bg: '#9CA3AF', text: '#FFFFFF' };
  return (
    <span style={{
      backgroundColor: style.bg,
      color: style.text,
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 'bold',
      textTransform: 'uppercase',
      display: 'inline-block',
      whiteSpace: 'nowrap'
    }}>
      {group}
    </span>
  );
};

function VehicleDetail() {
  // Get vehicle ID from URL params
  const { id: vehicleId } = useParams();

  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [selectedTeslaVehicleId, setSelectedTeslaVehicleId] = useState(null);
  const [vehicleData, setVehicleData] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [vehicleInfo, setVehicleInfo] = useState(null);
  const [chargingSessions, setChargingSessions] = useState([]);
  const [chargingStats, setChargingStats] = useState(null);
  const [cronStatus, setCronStatus] = useState(null);
  const [tcoData, setTcoData] = useState({
    ytd: { total: 0, operating: 0, financing: 0 },
    priorYear: { total: 0, operating: 0, financing: 0 },
    total: { total: 0, operating: 0, financing: 0 }
  });
  // Photo upload state for vehicle
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');

  // Valuation modal state
  const [showValuationModal, setShowValuationModal] = useState(false);
  const [showEditValuationsModal, setShowEditValuationsModal] = useState(false);

  // Manual entry form state (for Non-API / ICE vehicles)
  const [manualDate, setManualDate] = useState(() => {
    const d = new Date();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  });

  // Category choices state
  const [expenseCategories, setExpenseCategories] = useState({ items: [], noneItem: { value: 'none', label: 'None' } });
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [expandedParents, setExpandedParents] = useState({});

  // Asset/Linkage states
  const [showUtilityModal, setShowUtilityModal] = useState(false);
  const [utilityRow, setUtilityRow] = useState(null);
  const [allAssets, setAllAssets] = useState([]);
  const [valuations, setValuations] = useState({ book: [], market: [] });

  // Debt linking modal state
  const [showDebtModal, setShowDebtModal] = useState(false);
  const [linkingRow, setLinkingRow] = useState(null);
  const [debtList, setDebtList] = useState([]);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [debtCandidates, setDebtCandidates] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalPhase, setModalPhase] = useState('chooseDebt');
  const [scraping, setScraping] = useState(false);

  // Load assets for linkage resolution
  useEffect(() => {
    const loadAssets = async () => {
      try {
        const phys = await axios.get('/api/balance_sheet/assets/physical_assets/minimal/');
        setAllAssets(Array.isArray(phys.data) ? phys.data : []);
      } catch (e) {
        setAllAssets([]);
      }
    };
    loadAssets();
  }, []);

  const loadValuations = async () => {
    if (!vehicleId) return;
    try {
      const res = await axios.get(`/api/balance_sheet/assets/valuations/${vehicleId}/`);
      setValuations(res.data || { book: [], market: [] });
    } catch (e) {
      console.error('Failed to load valuations:', e);
      setValuations({ book: [], market: [] });
    }
  };

  useEffect(() => {
    loadValuations();
  }, [vehicleId]);

  // Memoized helper maps for fast asset lookups and labels
  const assetMaps = useMemo(() => {
    try {
      const list = Array.isArray(allAssets) ? allAssets : [];
      const byId = new Map();
      const resiAddressById = new Map();
      const structOrdinalById = new Map();
      const structIdsByProperty = new Map();

      for (const a of list) {
        const idStr = String(a.id);
        byId.set(idStr, a);
        const type2 = (a.asset_type_2 || a.type_2 || a.type2 || '').toLowerCase();
        if (type2 === 'resi_property') {
          const addr = a.address && a.address.street_address ? a.address.street_address : '';
          resiAddressById.set(idStr, addr);
        } else if (type2 === 'structure') {
          const rp = a.residential_property || a.residentialProperty || a.parent_property || a.parent || null;
          const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
          if (rpId != null) {
            const key = String(rpId);
            if (!structIdsByProperty.has(key)) structIdsByProperty.set(key, []);
            structIdsByProperty.get(key).push(a.id);
          }
        }
      }
      for (const ids of structIdsByProperty.values()) {
        ids.sort((a,b) => a - b);
        ids.forEach((sid, idx) => {
          structOrdinalById.set(String(sid), idx + 1);
        });
      }
      return { byId, resiAddressById, structOrdinalById };
    } catch (e) {
      return { byId: new Map(), resiAddressById: new Map(), structOrdinalById: new Map() };
    }
  }, [allAssets]);

  const buildAssetUrl = (asset) => {
    if (!asset) return null;
    const id = asset.id;
    const type2 = asset.asset_type_2 || asset.type_2 || asset.type2;
    const t = (type2 || '').toLowerCase();
    if (t === 'vehicle') return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
    if (t === 'resi_property') return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
    if (t === 'structure' || t === 'land') {
      const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent;
      const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
      if (rpId) return `/balance_sheet/assets/physical_assets/resi_properties/${rpId}`;
    }
    return null;
  };

  // Load category choices on mount
  useEffect(() => {
    const fetchCats = async () => {
      try {
        const res = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
        const tree = buildCategoryTreeSorted(res.data || []);
        setExpenseCategories(tree);
      } catch (e) {
        console.warn('Failed to load category choices');
      }
    };
    fetchCats();
  }, []);

  const openUtilitiesModal = (row) => {
    try {
      const r2 = {
        id: row.id,
        sourceType: row.sourceType || (row.source_account_display ? 'cash' : 'revolver'),
        description: row.description,
        amount: row.amount,
        transaction_date: row.transaction_date || row.post_date || '',
        post_date: row.post_date || '',
        date: row.transaction_date || row.post_date || '',
        linked_asset_id: row.linked_asset_id || '',
        linked_asset_expense_id: (row.linked_asset_expense_id != null && row.linked_asset_expense_id !== '') ? String(row.linked_asset_expense_id) : '',
        linked_utility_bill_id: row.linked_utility_bill_id || '',
        linked_recurring_series_id: row.linked_recurring_series_id || '',
        linked_property_id: row.parent_property_id || row.linked_property_id || '',
        expense_category: row.expense_category || '',
      };
      setUtilityRow(r2);
      setShowUtilityModal(true);
    } catch (e) {
      setUtilityRow({ id: row.id, description: row.description, amount: row.amount });
      setShowUtilityModal(true);
    }
  };

  const openDebtModalForRow = async (row) => {
    setModalError('');
    setModalLoading(true);
    setShowDebtModal(true);
    setLinkingRow(row);
    setSelectedDebt(null);
    setDebtCandidates([]);
    setModalPhase('chooseDebt');
    try {
      const res = await axios.get('/api/balance_sheet/financing/');
      const allDebts = Array.isArray(res.data) ? res.data : [];
      const nonRevolving = allDebts.filter(d => (d.debt_type === 'auto_loan' || d.debt_type === 'mortgage'));
      setDebtList(nonRevolving);
    } catch (e) {
      setModalError('Failed to load debts.');
    } finally {
      setModalLoading(false);
    }
  };

  const loadDebtCandidates = async (debt) => {
    if (!linkingRow || !debt) return;
    setModalError('');
    setModalLoading(true);
    try {
      const url = `/api/balance_sheet/assets/cash_accounts/transactions/${linkingRow.id}/candidate-debt-payments`;
      const params = { debt_type: debt.debt_type, debt_id: debt.id };
      const res = await axios.get(url, { params });
      const candidates = res.data && Array.isArray(res.data.candidates) ? res.data.candidates : [];
      setDebtCandidates(candidates);
      setModalPhase('chooseCandidate');
    } catch (e) {
      setModalError('Failed to load candidate payments.');
    } finally {
      setModalLoading(false);
    }
  };

  const confirmLinkToCandidate = async (candidate) => {
    if (!linkingRow || !selectedDebt || !candidate) return;
    setModalError('');
    setModalLoading(true);
    try {
      const url = `/api/balance_sheet/assets/cash_accounts/transactions/${linkingRow.id}/link-debt-payment/`;
      await axios.post(url, {
        debt_type: selectedDebt.debt_type,
        debt_id: selectedDebt.id,
        candidate_id: candidate.id,
      });
      // Refresh transactions
      loadMaintenanceRecords(); 
      setShowDebtModal(false);
      setLinkingRow(null);
    } catch (e) {
      setModalError('Failed to link payment.');
    } finally {
      setModalLoading(false);
    }
  };

  const updateExpenseCategory = async (row, newCat) => {
    try {
      const sourceType = row.sourceType || (row.source_account_display ? 'cash' : 'revolver');
      const api = sourceType === 'revolver' 
        ? `/api/balance_sheet/financing/revolvers/transactions/${row.id}/`
        : `/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/`;
      
      await axios.patch(api, { expense_category: newCat });
      
      // Update local state in table
      setMaintenanceRecords(prev => prev.map(t => (t.id === row.id ? { ...t, expense_category: newCat } : t)));
      
      const updatedRow = { ...row, expense_category: newCat, sourceType };
      
      // Prompts
      if (newCat === 'payments' && !row.linked_debt_id && sourceType === 'cash') {
        openDebtModalForRow(updatedRow);
      }
      
      const domain = getDomainForCategory(newCat);
      const isWaste = String(newCat).startsWith('waste_') || String(newCat).includes('sanitation');
      if (domain !== 'generic' || isWaste) {
        openUtilitiesModal(updatedRow);
      }
    } catch (e) {
      alert('Failed to update category.');
    } finally {
      setOpenDropdownId(null);
    }
  };

  const [manualOdometer, setManualOdometer] = useState('');
  const [manualFL, setManualFL] = useState('');
  const [manualFR, setManualFR] = useState('');
  const [manualRL, setManualRL] = useState('');
  const [manualRR, setManualRR] = useState('');
  const [manualSaving, setManualSaving] = useState(false);
  const [manualMessage, setManualMessage] = useState('');

  // Load vehicle info and check Tesla connection on component mount
  useEffect(() => {
    if (vehicleId) {
      loadVehicleInfo();
      loadMaintenanceRecords();
      loadChargingData();
      checkTokenAndLoadTeslaVehicles();
      loadTCO();
    }
  }, [vehicleId]);

  // Load cron status for Tesla-connected vehicles
  useEffect(() => {
    const fetchCron = async () => {
      if (!vehicleId) return;
      try {
        const res = await axios.get(`/api/vehicles/${vehicleId}/cron_status/`);
        setCronStatus(res.data);
      } catch (e) {
        console.error('Failed to load cron status', e);
      }
    };
    if (vehicleInfo?.api_connected && vehicleInfo?.api_provider === 'tesla') {
      fetchCron();
    }
  }, [vehicleId, vehicleInfo?.api_connected, vehicleInfo?.api_provider]);

  // Load the specific VehicleORM record
  const loadVehicleInfo = async () => {
    try {
      const res = await axios.get(`/api/vehicles/${vehicleId}/`);
      console.log('Vehicle info:', res.data);
      // Start with vehicle API data
      let merged = res.data || {};

      // Also fetch the corresponding PhysicalAsset to get local photo URLs
      try {
        const pa = await axios.get(`/api/balance_sheet/assets/physical_assets/${vehicleId}/`);
        if (pa && pa.data) {
          merged = { ...merged, photo: pa.data.photo || null, photo_thumb: pa.data.photo_thumb || null };
        }
      } catch (e) {
        // Non-fatal if the local asset fetch fails; keep existing fields
        console.warn('Failed to fetch local physical asset for photo fields', e);
      }

      setVehicleInfo(merged);

      // If this vehicle is already connected to Tesla, remember selected API vehicle, but always load history
      if (res.data.api_connected && res.data.api_provider === 'tesla') {
        setSelectedTeslaVehicleId(res.data.api_vehicle_id);
      }
      // Always load historical data regardless of API connection (manual entries or non-connected vehicles)
      await loadVehicleHistory();
      // Reload TCO after info loads in case propulsion type changed (affects include_asset_input)
      await loadTCO(merged);
    } catch (err) {
      console.error('Failed to load vehicle info:', err);
      setError('Failed to load vehicle information.');
    }
  };

  const runCarScraperAgent = async () => {
    try {
      setScraping(true);
      const res = await axios.post(`/api/vehicles/${vehicleId}/run_scraper_agent/`);
      if (res.data.error) {
        toast.error(`Scraper Error: ${res.data.error}`);
      } else {
        toast.success(`Scraper finished! Avg Price: ${formatCurrency(res.data.average_price)} (n=${res.data.count})`);
        // Refresh info to show new valuation if needed
        await Promise.all([loadVehicleInfo(), loadValuations()]);
      }
    } catch (e) {
      console.error('Failed to run scraper agent', e);
      toast.error('Failed to run car scraper agent.');
    } finally {
      setScraping(false);
    }
  };

  const loadTCO = async (infoOverride = null) => {
    try {
      const info = infoOverride || vehicleInfo;
      const now = new Date();
      const currentYear = now.getFullYear();
      const jan1Current = `${currentYear}-01-01`;
      const dec31Prior = `${currentYear - 1}-12-31`;
      const jan1Prior = `${currentYear - 1}-01-01`;

      // Use a helper to fetch and sum
      const fetchSum = async (start, end) => {
        const params = {
          only_txn_backed: 1,
          include_asset_input: 1
        };
        if (start) params.start_date = start;
        if (end) params.end_date = end;

        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${vehicleId}/linked-expenses/`, { params });
        const actuals = res.data?.actual || [];
        
        // TCO includes Operating expenses, Financing (Interest/Fees), and CapEx (Enhancements, Principal)
        // We exclude 'initial_purchase' from CapEx to avoid massive price inflation in Total TCO
        const opFiltered = actuals.filter(item => item.expense_group === 'Operating');
        const finFiltered = actuals.filter(item => item.expense_group === 'Financing');
        const capexFiltered = actuals.filter(item => item.expense_group === 'CapEx' && item.expense_category !== 'initial_purchase');
        
        const opSum = opFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
        const finSum = finFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
        const capexSum = capexFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
        
        return { total: opSum + finSum + capexSum, operating: opSum, financing: finSum, capex: capexSum };
      };

      const [ytd, prior, total] = await Promise.all([
        fetchSum(jan1Current, null),
        fetchSum(jan1Prior, dec31Prior),
        fetchSum(null, null)
      ]);

      setTcoData({ ytd, priorYear: prior, total });
    } catch (e) {
      console.error('Failed to load TCO metrics', e);
    }
  };

  const openValuationModal = () => {
    setShowValuationModal(true);
  };

  const closeValuationModal = async () => {
    setShowValuationModal(false);
    try {
      // Refresh vehicle info and valuations after adding a valuation
      await Promise.all([
        loadVehicleInfo(),
        loadValuations()
      ]);
    } catch (e) {
      // non-fatal
      console.warn('Failed to refresh vehicle after valuation submit/close', e);
    }
  };

  // Upload/replace the single photo for this vehicle (uses local backend physical_assets endpoint)
  const handlePhotoFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file || !vehicleId) return;
    setPhotoError('');
    const allowed = ['image/jpeg','image/png','image/webp'];
    if (!allowed.includes(file.type)) {
      setPhotoError('Please select a JPG, PNG, or WEBP image.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setPhotoError('Image exceeds 10MB limit.');
      return;
    }
    try {
      setPhotoUploading(true);
      const form = new FormData();
      form.append('photo', file);
      await axios.post(`/api/balance_sheet/assets/physical_assets/${vehicleId}/photo/`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      // Refresh info so photo/photo_thumb becomes visible (pull from local PhysicalAsset)
      try {
        const pa = await axios.get(`/api/balance_sheet/assets/physical_assets/${vehicleId}/`);
        if (pa && pa.data) {
          setVehicleInfo(prev => ({ ...(prev || {}), photo: pa.data.photo || null, photo_thumb: pa.data.photo_thumb || null }));
        }
      } catch (e2) {
        // Fallback: reload full vehicle info (will attempt to merge again)
        await loadVehicleInfo();
      }
    } catch (err) {
      console.error('Vehicle photo upload failed', err);
      setPhotoError('Failed to upload photo.');
    } finally {
      setPhotoUploading(false);
      try { e.target.value = ''; } catch(_) {}
    }
  };

  // Check token validity and load Tesla vehicles if valid
  const checkTokenAndLoadTeslaVehicles = async () => {
    try {
      const res = await axios.get(`/api/vehicles/check_token_validity/`);
      const data = res.data;

      if (data.is_valid) {
        await loadAvailableTeslaVehicles();
      }
    } catch (err) {
      console.error('Error checking token validity:', err);
    }
  };

  // Load available Tesla vehicles from API
  const loadAvailableTeslaVehicles = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/vehicles/available/?provider=tesla');
      console.log('Available Tesla vehicles:', res.data);
      setVehicles(res.data.vehicles || []);
    } catch (err) {
      console.error('Failed to load Tesla vehicles:', err);
      setError('Failed to load available Tesla vehicles.');
    } finally {
      setLoading(false);
    }
  };

  // Link selected Tesla vehicle to this VehicleORM record
  const linkTeslaVehicle = async () => {
    if (!selectedTeslaVehicleId) {
      setError('Please select a Tesla vehicle first.');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post('/api/vehicles/link/', {
        vehicle_orm_id: parseInt(vehicleId),
        api_provider: 'tesla',
        api_vehicle_id: selectedTeslaVehicleId
      });

      if (res.data.success) {
        alert('Tesla vehicle linked successfully!');
        await loadVehicleInfo(); // Reload to show updated connection status
      } else {
        setError('Failed to link Tesla vehicle.');
      }
    } catch (err) {
      console.error('Failed to link Tesla vehicle:', err);
      setError('Failed to link Tesla vehicle.');
    } finally {
      setLoading(false);
    }
  };

  const syncVehicleData = async () => {
      try {
          const response = await axios.post('/api/vehicles/sync/', {}, {
              timeout: 90000
          });

          const data = response.data;
          console.log('=== ACTUAL RESPONSE FROM BACKEND ===');
          console.log('Full response:', data);
          console.log('Response keys:', Object.keys(data));

          // Check if there's an auth_url anywhere
          if (data.auth_url) {
              alert('Found direct auth_url');
              window.location.href = data.auth_url;
              return;
          }

          // Check in results array
          if (data.results && data.results.length > 0) {
              console.log('Found results array with', data.results.length, 'items');
              const result = data.results[0];
              console.log('First result:', result);

              if (result.requires_auth && result.auth_url) {
                  alert('Found auth_url in results, redirecting...');
                  window.location.href = result.auth_url;
                  return;
              }

              if (result.success) {
                  alert('Sync was successful!');
              } else {
                  alert('Sync failed: ' + result.error);
              }
          }

          alert('No redirect needed, check console for full response');

      } catch (error) {
          alert('Error: ' + error.message);
          console.error('Sync error:', error);
      }
  };

  // Load maintenance records for this vehicle
  const loadMaintenanceRecords = async () => {
    if (!vehicleId) return;

    try {
      const res = await axios.get(`/api/vehicles/${vehicleId}/maintenance/`);
      console.log('Maintenance records:', res.data);
      setMaintenanceRecords(res.data.records || []);
    } catch (err) {
      console.error('Failed to load maintenance records:', err);
      // Don't set error here since this might be expected if no records exist yet
      setMaintenanceRecords([]);
    }
  };

  // Load historical data for this vehicle
  const loadVehicleHistory = async () => {
    if (!vehicleId) return;

    setLoading(true);
    try {
      const res = await axios.get(`/api/vehicles/${vehicleId}/history/?limit=50`);
      console.log('Vehicle history:', res.data);
      setVehicleData(res.data.records || []);
    } catch (err) {
      console.error('Failed to load vehicle history:', err);
      // Don't set error here since this might be expected if no history exists yet
      setVehicleData([]);
    } finally {
      setLoading(false);
    }
  };

  const submitManualVehicleData = async () => {
    if (!vehicleId) return;
    setManualSaving(true);
    setManualMessage('');
    try {
      const payload = {
        recorded_date: manualDate,
        odometer_miles: manualOdometer !== '' ? Number(manualOdometer) : null,
        tire_pressure_front_left: manualFL !== '' ? Number(manualFL) : null,
        tire_pressure_front_right: manualFR !== '' ? Number(manualFR) : null,
        tire_pressure_rear_left: manualRL !== '' ? Number(manualRL) : null,
        tire_pressure_rear_right: manualRR !== '' ? Number(manualRR) : null,
      };
      await axios.post(`/api/vehicles/${vehicleId}/history/`, payload);
      setManualMessage('Saved!');
      // Refresh history
      await loadVehicleHistory();
      // Refresh TCO
      await loadTCO();
      // Clear form
      setManualOdometer(''); setManualFL(''); setManualFR(''); setManualRL(''); setManualRR('');
    } catch (err) {
      console.error('Failed to save manual vehicle data:', err);
      setManualMessage('Error saving data');
    } finally {
      setManualSaving(false);
    }
  };

  const loadChargingData = async () => {
    if (!vehicleId) return;

    try {
      // Load charging sessions
      const sessionsRes = await axios.get(`/api/vehicles/${vehicleId}/charging/`);
      setChargingSessions(sessionsRes.data.sessions || []);

      // Load charging stats
      const statsRes = await axios.get(`/api/vehicles/${vehicleId}/charging/stats/`);
      setChargingStats(statsRes.data);
    } catch (err) {
      console.error('Failed to load charging data:', err);
      setChargingSessions([]);
      setChargingStats(null);
    }
  };

  const syncChargingHistory = async (fullSync = false) => {
    if (!vehicleInfo?.api_connected) {
      setError('Vehicle must be connected to Tesla API first.');
      return;
    }

    setLoading(true);
    try {
      const url = `/api/vehicles/${vehicleId}/charging/` + (fullSync ? '?full_sync=true' : '');
      const res = await axios.post(url);
      console.log('Charging sync results:', res.data);

      if (res.data.success) {
        await loadChargingData(); // Reload data
        alert(`Successfully synced ${res.data.synced_sessions} charging sessions!`);
      }
    } catch (err) {
      console.error('Failed to sync charging data:', err);
      setError('Failed to sync charging data.');
    } finally {
      setLoading(false);
    }
  };

  // Start Tesla OAuth authorization
  const startAuthorization = async () => {
    try {
      const res = await axios.get('/api/vehicles/tesla/authorize/');
      window.location.href = res.data.auth_url;
    } catch (err) {
      console.error('Error getting auth URL', err);
      setError('Failed to start authorization.');
    }
  };

  // Handle authorization flow
  const handleAuthorization = async () => {
    try {
      const res = await axios.get(`/api/vehicles/check_token_validity/`);
      const data = res.data;

      if (data.is_valid) {
        // Token is valid, load vehicles
        await loadAvailableTeslaVehicles();
      } else {
        // Token invalid, start OAuth flow
        await startAuthorization();
      }
    } catch (err) {
      console.error('Error handling authorization:', err);
      await startAuthorization(); // Fallback to start flow
    }
  };

  // Filter vehicle data to show only latest record per day
  const getLatestRecordPerDay = (records) => {
    const recordsByDate = {};

    records.forEach(record => {
      const date = new Date(record.recorded_at).toDateString();

      // If no record for this date, or this record is more recent, use it
      if (!recordsByDate[date] ||
          new Date(record.recorded_at) > new Date(recordsByDate[date].recorded_at)) {
        recordsByDate[date] = record;
      }
    });

    // Convert back to array and sort by date (newest first)
    return Object.values(recordsByDate).sort(
      (a, b) => new Date(b.recorded_at) - new Date(a.recorded_at)
    );
  };

  // Calculate maintenance totals
  const calculateMaintenanceTotals = () => {
    if (maintenanceRecords.length === 0) {
      return {
        totalRecords: 0,
        totalCost: 0,
        totalLaborCost: 0,
        totalPartsCost: 0,
        totalTaxAmount: 0,
        totalOtherFees: 0
      };
    }

    return maintenanceRecords.reduce((totals, record) => {
      return {
        totalRecords: totals.totalRecords + 1,
        totalCost: totals.totalCost + (record.total_cost || 0),
        totalLaborCost: totals.totalLaborCost + (record.labor_cost || 0),
        totalPartsCost: totals.totalPartsCost + (record.parts_cost || 0),
        totalTaxAmount: totals.totalTaxAmount + (record.tax_amount || 0),
        totalOtherFees: totals.totalOtherFees + (record.other_fees || 0)
      };
    }, {
      totalRecords: 0,
      totalCost: 0,
      totalLaborCost: 0,
      totalPartsCost: 0,
      totalTaxAmount: 0,
      totalOtherFees: 0
    });
  };

  const maintenanceTotals = calculateMaintenanceTotals();

  // Format currency for display
  const formatCurrencyLocal = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return `${Math.round(amount).toLocaleString()}`;
  };

  // Get filtered data for display
  const displayData = getLatestRecordPerDay(vehicleData);
  const isEV = vehicleInfo?.propulsion_type === 'EV';

  // Format tire pressure data for display
  const formatTirePressure = (record) => {
    const pressures = record.tire_pressure;
    if (!pressures || (!pressures.front_left && !pressures.front_right && !pressures.rear_left && !pressures.rear_right)) {
      return 'No data';
    }

    const recommendedPsi = 42;

    const formatSingleTire = (pressure, label) => {
      if (!pressure) return `${label}: --`;

      const diff = recommendedPsi - pressure;
      let style = {};

      if (diff > 3) {
        // More than 3 PSI below recommended - RED alert
        style = { backgroundColor: '#ffebee', color: '#c62828', fontWeight: 'bold' };
      } else if (diff > 0) {
        // 1-3 PSI below recommended - YELLOW warning
        style = { backgroundColor: '#fff8e1', color: '#f57f17', fontWeight: 'bold' };
      }

      return (
        <span style={style}>
          {label}: {pressure}
        </span>
      );
    };

    return (
      <div style={{ fontSize: '12px' }}>
        {formatSingleTire(pressures.front_left, 'FL')} | {' '}
        {formatSingleTire(pressures.front_right, 'FR')} | {' '}
        {formatSingleTire(pressures.rear_left, 'RL')} | {' '}
        {formatSingleTire(pressures.rear_right, 'RR')}
      </div>
    );
  };

  // Format battery info for display
  const formatBatteryInfo = (record) => {
    if (!record.battery_level) return 'No data';
    return `${record.battery_level}%${record.battery_range ? ` (${record.battery_range} mi)` : ''}`;
  };

  // Clear token for testing
  const clearTeslaToken = async () => {
    try {
      const res = await axios.post('/api/vehicles/tesla/clear-token/');
      console.log('Token cleared:', res.data);
      setVehicles([]);
      setVehicleData([]);
      setSelectedTeslaVehicleId(null);
      alert('Token cleared! Click "Connect Tesla" to authorize with a different account.');
    } catch (err) {
      console.error('Failed to clear token:', err);
    }
  };

  // Graph view state: 'expenses' | 'mileage' | 'tire_pressure' | 'charging'
  const [activeGraph, setActiveGraph] = useState('expenses');

  // Track date ranges from charts for syncing tables
  const [expenseFrom, setExpenseFrom] = useState(null);
  const [expenseTo, setExpenseTo] = useState(null);
  const [selectedRecurringName, setSelectedRecurringName] = useState(null);
  const [chargeFrom, setChargeFrom] = useState(null);
  const [chargeTo, setChargeTo] = useState(null);

  // Shared date range for Mileage and Tire Pressure (match Expenses defaults: YTD)
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const jan1 = new Date(now.getFullYear(), 0, 1);
    return jan1.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    const dec31 = new Date(now.getFullYear(), 11, 31);
    return dec31.toISOString().slice(0, 10);
  });

  const withinRange = (dt) => {
    try {
      const d = new Date(dt);
      const start = new Date(fromDate + 'T00:00:00');
      const end = new Date(toDate + 'T23:59:59');
      return d >= start && d <= end;
    } catch (e) {
      return true;
    }
  };

  // Prepare mileage series with true linear extrapolation back to purchase date
  const buildMileageSeries = () => {
    const points = [];

    // Parse selected range boundaries once
    const rangeStart = new Date(fromDate + 'T00:00:00');
    const rangeEnd = new Date(toDate + 'T23:59:59');

    // Sort odometer records ascending by recorded_at
    const sorted = [...displayData]
      .filter(r => r.odometer_miles !== null && r.odometer_miles !== undefined)
      .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));

    // Determine purchase anchor robustly
    const rawPd = vehicleInfo?.purchase_date || vehicleInfo?.initial_purchase_price?.payment_date;
    let pd = null;
    if (rawPd) {
      const pdStr = typeof rawPd === 'string' ? rawPd : String(rawPd);
      const candidate = new Date(pdStr.includes('T') ? pdStr : `${pdStr}T00:00:00`);
      if (!isNaN(candidate.getTime())) {
        pd = candidate;
      } else {
        const fallback = new Date(pdStr); // last-resort parse
        pd = isNaN(fallback.getTime()) ? null : fallback;
      }
    }
    let mp = vehicleInfo?.mileage_at_purchase;
    // If vehicle is NEW and mileage_at_purchase is missing, assume 0 (case-insensitive)
    const isNew = (vehicleInfo?.new_or_used || '').toString().toLowerCase() === 'new';
    if ((mp === null || mp === undefined) && isNew) {
      mp = 0;
    }

    const hasPurchaseAnchor = pd !== null && mp !== null && mp !== undefined;
    const purchasePoint = hasPurchaseAnchor ? { x: pd, y: Number(mp) } : null;

    // Identify the earliest known odometer point (after purchase in most cases)
    const firstKnown = sorted.length > 0 ? { x: new Date(sorted[0].recorded_at), y: Number(sorted[0].odometer_miles) } : null;

    // If the selected range starts after purchase but before the first known reading,
    // insert a synthetic point at the range start using linear interpolation between
    // the purchase anchor and the first known reading. This renders the backward extrapolation
    // segment even when the purchase date lies outside the current view window.
    if (hasPurchaseAnchor && firstKnown) {
      const t0 = purchasePoint.x.getTime();
      const t1 = firstKnown.x.getTime();
      if (t1 > t0) {
        const rs = rangeStart.getTime();
        if (rs >= t0 && rs <= t1) {
          const y0 = purchasePoint.y;
          const y1 = firstKnown.y;
          const yAtRangeStart = y0 + (y1 - y0) * ((rs - t0) / (t1 - t0));
          // Add synthetic boundary point at the start of the selected range (inclusive)
          points.push({ x: new Date(rangeStart), y: yAtRangeStart });
        }
      }
    }

    // Add purchase anchor if within or before the range (kept for full-window views)
    if (hasPurchaseAnchor) {
      points.push(purchasePoint);
    }

    // Append known odometer points
    for (const r of sorted) {
      const dt = new Date(r.recorded_at);
      // Only include points up to the selected range end; we don't forward-extrapolate beyond data
      if (dt <= rangeEnd) {
        points.push({ x: dt, y: Number(r.odometer_miles) });
      }
    }

    // De-duplicate by date (keep last for duplicate same day)
    const map = new Map();
    for (const p of points) {
      const key = p.x.toISOString().slice(0, 10);
      map.set(key, p);
    }
    let uniq = Array.from(map.values()).sort((a, b) => a.x - b.x);

    // Filter to selected date range (inclusive)
    uniq = uniq.filter(p => p.x >= rangeStart && p.x <= rangeEnd);

    return uniq;
  };

  // Prepare tire pressure series for 4 tires, filtered to date range
  const buildTirePressureSeries = () => {
    const sorted = [...displayData]
      .filter(r => {
        const tp = r.tire_pressure || r;
        const hasAny = tp.tire_pressure_front_left || tp.tire_pressure_front_right || tp.tire_pressure_rear_left || tp.tire_pressure_rear_right || (tp.front_left || tp.front_right || tp.rear_left || tp.rear_right);
        return hasAny && withinRange(new Date(r.recorded_at));
      })
      .sort((a, b) => new Date(a.recorded_at) - new Date(b.recorded_at));

    const x = [];
    const fl = []; const fr = []; const rl = []; const rr = [];
    for (const r of sorted) {
      const tp = r.tire_pressure || r;
      const vfl = tp.front_left ?? tp.tire_pressure_front_left ?? null;
      const vfr = tp.front_right ?? tp.tire_pressure_front_right ?? null;
      const vrl = tp.rear_left ?? tp.tire_pressure_rear_left ?? null;
      const vrr = tp.rear_right ?? tp.tire_pressure_rear_right ?? null;
      x.push(new Date(r.recorded_at));
      fl.push(vfl !== null ? Number(vfl) : null);
      fr.push(vfr !== null ? Number(vfr) : null);
      rl.push(vrl !== null ? Number(vrl) : null);
      rr.push(vrr !== null ? Number(vrr) : null);
    }
    return { x, fl, fr, rl, rr };
  };

  const mileageSeries = buildMileageSeries();
  const tireSeries = buildTirePressureSeries();

  const valuationSeries = useMemo(() => {
    const book = (valuations.book || []).map(v => ({
      x: new Date(v.date),
      y: Number(v.value),
      type: 'Book Value'
    }));
    const market = (valuations.market || []).map(v => ({
      x: new Date(v.date),
      y: Number(v.value),
      type: 'Market Value'
    }));
    // Sort combined or separate? Usually Plotly handles sorted X better
    const sortedBook = book.sort((a, b) => a.x - b.x);
    const sortedMarket = market.sort((a, b) => a.x - b.x);
    return { book: sortedBook, market: sortedMarket };
  }, [valuations]);

  const recommendedPsi = 42;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '10px' }}>
        <h1 style={{ margin: 0 }}>{vehicleInfo ? `${vehicleInfo.vehicle_year || ''} ${vehicleInfo.vehicle_make || ''} ${vehicleInfo.vehicle_model || ''}`.trim() || 'Vehicle' : 'Vehicle'}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Weekly cron status strip for Tesla-connected vehicles */}
          {vehicleInfo?.api_connected && vehicleInfo?.api_provider === 'tesla' && cronStatus?.days && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '12px' }} title="Daily cron jobs: green o=ok, orange e=error, red x=miss">
              {cronStatus.days.map((d, idx) => {
                const day = new Date(d.date + 'T00:00:00');
                const label = day.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                const mk = (code) => {
                  const base = {
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: 3, fontSize: 12, fontWeight: 700,
                    color: '#111',
                  };
                  if (code === 'o') return { ...base, backgroundColor: '#34d399' }; // green
                  if (code === 'e') return { ...base, backgroundColor: '#f59e0b' }; // orange
                  return { ...base, backgroundColor: '#ef4444', color: 'white' };   // red
                };
                return (
                  <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 2 }} title={`${label} vehicle fetch`}>
                      <span style={mk(d.vehicle_fetch)}>{d.vehicle_fetch}</span>
                      <span style={mk(d.charging_fetch)} title={`${label} charging fetch`}>{d.charging_fetch}</span>
                    </div>
                    <span style={{ fontSize: 10, color: '#9CA3AF', marginTop: 2 }}>{label}</span>
                  </div>
                );
              })}
            </div>
          )}
          {vehicleInfo?.api_connected && (
            <>
          <button onClick={syncVehicleData} disabled={loading} className="btn btn-outline-primary btn-sm" style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
            {loading ? 'Syncing...' : 'Sync Vehicle Data'}
          </button>

          {isEV && (
            <button onClick={syncChargingHistory} disabled={loading} className="btn btn-outline-success btn-sm" style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Syncing...' : 'Sync Charge Data'}
            </button>
          )}

          <button onClick={loadVehicleHistory} disabled={loading} className="btn btn-outline-info btn-sm" style={{ cursor: loading ? 'not-allowed' : 'pointer' }}>
            Refresh History
          </button>

          <button onClick={runCarScraperAgent} disabled={scraping} className="btn btn-primary btn-sm">
            {scraping ? 'Scraping...' : 'Run Price Scraper AI'}
          </button>

          <button onClick={clearTeslaToken} className="btn btn-outline-danger btn-sm">
            Clear Token
          </button>
            </>
          )}
        </div>
      </div>

      <Modal isOpen={showValuationModal} onClose={closeValuationModal} title="Add Valuation">
        <ValuationForm assetId={Number(vehicleId)} onClose={closeValuationModal} />
      </Modal>

      <ValuationsModal
        isOpen={showEditValuationsModal}
        onClose={() => setShowEditValuationsModal(false)}
        assetId={Number(vehicleId)}
        onValuationsUpdated={loadValuations}
      />

      {/* Vehicle Info + Photo (summary left, photo right) */}
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="dark" />
      {vehicleInfo && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16, alignItems: 'stretch', marginBottom: 20 }}>
          <div style={{ backgroundColor: '#1f2937', padding: '15px', borderRadius: '4px', border: '1px solid #374151', color: '#e5e7eb' }}>
            <p style={{ marginBottom: '8px' }}><strong>Propulsion:</strong> {vehicleInfo.propulsion_type || 'N/A'}</p>
            <p style={{ marginBottom: '8px' }}>
              <strong>Age:</strong>{' '}
              {vehicleInfo.age_years !== null && vehicleInfo.age_years !== undefined ? `${vehicleInfo.age_years} yrs` : 'N/A'}
            </p>
            <p style={{ marginBottom: '8px' }}>
              <strong>Last Odometer:</strong>{' '}
              {displayData.length > 0 && displayData[0].odometer_miles ? (
                <>
                  {displayData[0].odometer_miles.toLocaleString()} mi
                  <span style={{ color: '#9CA3AF', marginLeft: '8px' }}>
                    ({new Date(displayData[0].recorded_at).toLocaleDateString()})
                  </span>
                </>
              ) : 'N/A'}
            </p>
            <p style={{ marginBottom: '8px' }}>
              <strong>Average Annual Mileage:</strong>{' '}
              {vehicleInfo.average_annual_mileage !== null && vehicleInfo.average_annual_mileage !== undefined
                ? `${vehicleInfo.average_annual_mileage.toLocaleString()} mi/yr`
                : 'N/A'}
            </p>
            <p style={{ marginBottom: '8px' }}>
              <strong>Outstanding Auto Loan:</strong>{' '}
              {typeof vehicleInfo?.current_outstanding_debt === 'number' ? (
                vehicleInfo?.linked_auto_loan_ids && vehicleInfo.linked_auto_loan_ids.length > 0 ? (
                  <a href={`/balance_sheet/financing/auto_loans/${vehicleInfo.linked_auto_loan_ids[0]}`} style={{ color: '#93c5fd' }}>
                    {formatCurrency(vehicleInfo.current_outstanding_debt)}
                  </a>
                ) : (
                  `{formatCurrency(vehicleInfo.current_outstanding_debt)}`
                )
              ) : 'N/A'}
            </p>
            <p style={{ marginBottom: '8px' }}>
              <strong>TCO (YTD / Prior Year / Total):</strong>{' '}
              <span style={{ color: '#fbbf24' }} title={`Op: $${formatCurrencyLocal(tcoData.ytd.operating)}, Fin: $${formatCurrencyLocal(tcoData.ytd.financing)}, CapEx: $${formatCurrencyLocal(tcoData.ytd.capex)}`}>
                ${formatCurrencyLocal(tcoData.ytd.total)}
              </span>
              {' / '}
              <span title={`Op: $${formatCurrencyLocal(tcoData.priorYear.operating)}, Fin: $${formatCurrencyLocal(tcoData.priorYear.financing)}, CapEx: $${formatCurrencyLocal(tcoData.priorYear.capex)}`}>
                ${formatCurrencyLocal(tcoData.priorYear.total)}
              </span>
              {' / '}
              <span style={{ fontWeight: 'bold' }} title={`Op: $${formatCurrencyLocal(tcoData.total.operating)}, Fin: $${formatCurrencyLocal(tcoData.total.financing)}, CapEx: $${formatCurrencyLocal(tcoData.total.capex)}`}>
                ${formatCurrencyLocal(tcoData.total.total)}
              </span>
            </p>
            <p style={{ marginBottom: '8px' }}>
              <strong>Scraping Mileage Threshold:</strong>{' '}
              <input
                type="number"
                value={vehicleInfo.mileage_threshold || 2500}
                onChange={async (e) => {
                  const val = parseInt(e.target.value);
                  setVehicleInfo({ ...vehicleInfo, mileage_threshold: val });
                  try {
                    await axios.patch(`/api/vehicles/${vehicleId}/`, { mileage_threshold: val });
                  } catch (err) {
                    console.error("Failed to update mileage threshold", err);
                  }
                }}
                style={{ width: '80px', background: '#374151', color: '#fff', border: '1px solid #4b5563', borderRadius: '4px', padding: '2px 4px' }}
              />
              <span style={{ marginLeft: '4px', color: '#9ca3af', fontSize: '12px' }}>miles</span>
            </p>
            {isEV && (
              <p><strong>API Connection:</strong>
                {vehicleInfo.api_connected ? (
                  <span style={{ color: '#34d399', marginLeft: '10px' }}>
                    ✅ Connected to {vehicleInfo.api_display_name} (ID: {vehicleInfo.api_vehicle_id})
                  </span>
                ) : (
                  <span style={{ color: '#f87171', marginLeft: '10px' }}>❌ Not connected</span>
                )}
              </p>
            )}
            {vehicleInfo.api_last_sync && (
              <p><strong>Last Sync:</strong> {new Date(vehicleInfo.api_last_sync).toLocaleString()}</p>
            )}
          </div>
          <div style={{ position: 'relative' }}>
            <div style={{ border: '1px solid #374151', background: '#0b1220', borderRadius: 8, overflow: 'hidden' }}>
              {vehicleInfo?.photo_thumb ? (
                <img src={vehicleInfo.photo_thumb} alt="Vehicle" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} loading="lazy" />
              ) : (
                <div style={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14 }}>
                  No photo yet
                </div>
              )}
            </div>
            <label style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 20, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}>
              {photoUploading ? 'Uploading…' : (vehicleInfo?.photo ? 'Change photo' : 'Upload photo')}
              <input type="file" accept="image/*" onChange={handlePhotoFileChange} style={{ display: 'none' }} />
            </label>
            {photoError && <div style={{ color: '#f87171', fontSize: 12, marginTop: 6 }}>{photoError}</div>}
          </div>
        </div>
      )}

      {/* Graph View Tabs */}
      <div style={{ marginTop: '16px', marginBottom: '12px' }}>
        <button onClick={() => setActiveGraph('expenses')} className={activeGraph==='expenses' ? 'tab-active' : 'tab'} style={{ marginRight: 8 }}>
          Expenses
        </button>
        <button onClick={() => setActiveGraph('recurring_expenses')} className={activeGraph==='recurring_expenses' ? 'tab-active' : 'tab'} style={{ marginRight: 8 }}>
          Recurring
        </button>
        <button onClick={() => setActiveGraph('mileage')} className={activeGraph==='mileage' ? 'tab-active' : 'tab'} style={{ marginRight: 8 }}>
          Mileage
        </button>
        <button onClick={() => setActiveGraph('tire_pressure')} className={activeGraph==='tire_pressure' ? 'tab-active' : 'tab'} style={{ marginRight: 8 }}>
          Tire Pressure
        </button>
        <button onClick={() => setActiveGraph('valuations')} className={activeGraph==='valuations' ? 'tab-active' : 'tab'} style={{ marginRight: 8 }}>
          Valuations
        </button>
        {isEV && (
          <button onClick={() => setActiveGraph('charging')} className={activeGraph==='charging' ? 'tab-active' : 'tab'} style={{ marginRight: 8 }}>
            Charging
          </button>
        )}
        <button onClick={() => setActiveGraph('todo')} className={activeGraph==='todo' ? 'tab-active' : 'tab'} style={{ marginRight: 8 }}>
          To-Do List
        </button>
      </div>

      {/* Graphs */}
      {activeGraph === 'expenses' && (
        <div style={{ marginTop: '12px', marginBottom: '24px' }}>
          <AggregatedAssetExpenseChart
            assetIds={[Number(vehicleId)]}
            chartTitle="Vehicle Expenses"
            onRangeChange={(from,to)=>{ setExpenseFrom(from); setExpenseTo(to); }}
            preferAssetExpenseTypes={true}
          />
          {/* Expenses Transactions Table below the chart */}
          <ExpensesTransactionsTable
            assetId={Number(vehicleId)}
            fromDate={expenseFrom}
            toDate={expenseTo}
            onExpenseChange={() => loadTCO()}
            assetMaps={assetMaps}
            assetName={vehicleInfo ? (vehicleInfo.year + ' ' + vehicleInfo.make + ' ' + vehicleInfo.model) : ''}
            allAssets={allAssets}
            vehicleInfo={vehicleInfo}
            vehicleId={vehicleId}
            onSelectRecurring={(name) => {
              setSelectedRecurringName(name);
              setActiveGraph('recurring_expenses');
            }}
          />
        </div>
      )}

      {activeGraph === 'recurring_expenses' && (
        <div style={{ marginTop: '12px', marginBottom: '24px' }}>
          <RecurringManager
            assetId={Number(vehicleId)}
            isProperty={false}
            initialSelectedName={selectedRecurringName}
          />
        </div>
      )}

      {activeGraph === 'todo' && (
        <div style={{ marginTop: '12px', marginBottom: '24px' }}>
          <PhysicalAssetToDoList assetId={vehicleId} assetType="vehicle" />
        </div>
      )}

      {activeGraph === 'mileage' && (
        <div style={{ marginTop: '12px', marginBottom: '24px', width: '100%', display: 'block' }}>
          <div style={{ height: 400, background: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ color: '#e5e7eb', fontWeight: 600 }}>Mileage Over Time</div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input className="form-control form-control-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                <input className="form-control form-control-sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
            </div>
            {mileageSeries.length === 0 ? (
              <div style={{ color: '#9CA3AF' }}>No mileage data for the selected range.</div>
            ) : (
              <Plot
                data={[{
                  x: mileageSeries.map(p => p.x),
                  y: mileageSeries.map(p => p.y),
                  type: 'scatter',
                  mode: 'lines+markers',
                  name: 'Odometer',
                  line: { color: '#60a5fa' },
                  marker: { color: '#60a5fa' }
                }]}
                layout={{
                  xaxis: { title: 'Date' },
                  yaxis: { title: 'Odometer (mi)' },
                  template: 'plotly_dark',
                  plot_bgcolor: '#0b1220',
                  paper_bgcolor: '#0b1220',
                  autosize: true,
                  margin: { l: 40, r: 10, b: 40, t: 10 }
                }}
                useResizeHandler={true}
                style={{ width: '100%', height: '340px' }}
              />
            )}
          </div>
        </div>
      )}

      {activeGraph === 'charging' && isEV && (
        <div style={{ marginTop: '12px', marginBottom: '24px' }}>
          <ChargingEnergyChart vehicleId={vehicleId} chartTitle="Charging Energy (kWh)" onRangeChange={(from,to)=>{ setChargeFrom(from); setChargeTo(to); }} />
        </div>
      )}

      {activeGraph === 'tire_pressure' && (
        <div style={{ marginTop: '12px', marginBottom: '24px', width: '100%', display: 'block' }}>
          <div style={{ height: 400, background: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ color: '#e5e7eb', fontWeight: 600 }}>Tire Pressure Over Time</div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <input className="form-control form-control-sm" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                <input className="form-control form-control-sm" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
              </div>
            </div>
            {tireSeries.x.length === 0 ? (
              <div style={{ color: '#9CA3AF' }}>No tire pressure data for the selected range.</div>
            ) : (
              <Plot
                data={[
                  { x: tireSeries.x, y: tireSeries.fl, type: 'scatter', mode: 'lines+markers', name: 'Front Left', line: { color: '#34d399' } },
                  { x: tireSeries.x, y: tireSeries.fr, type: 'scatter', mode: 'lines+markers', name: 'Front Right', line: { color: '#10b981' } },
                  { x: tireSeries.x, y: tireSeries.rl, type: 'scatter', mode: 'lines+markers', name: 'Rear Left', line: { color: '#f59e0b' } },
                  { x: tireSeries.x, y: tireSeries.rr, type: 'scatter', mode: 'lines+markers', name: 'Rear Right', line: { color: '#f97316' } },
                  { x: tireSeries.x, y: tireSeries.x.map(() => recommendedPsi), type: 'scatter', mode: 'lines', name: 'Recommended 42 psi', line: { color: '#9CA3AF', dash: 'dash' } }
                ]}
                layout={{
                  xaxis: { title: 'Date' },
                  yaxis: { title: 'PSI', rangemode: 'tozero' },
                  template: 'plotly_dark',
                  plot_bgcolor: '#0b1220',
                  paper_bgcolor: '#0b1220',
                  autosize: true,
                  margin: { l: 40, r: 10, b: 40, t: 10 }
                }}
                useResizeHandler={true}
                style={{ width: '100%', height: '340px' }}
              />
            )}
          </div>
        </div>
      )}

      {activeGraph === 'valuations' && (
        <div style={{ marginTop: '12px', marginBottom: '24px', width: '100%', display: 'block' }}>
          <div style={{ height: 400, background: '#0b1220', border: '1px solid #374151', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
              <div style={{ color: '#e5e7eb', fontWeight: 600 }}>Valuations Over Time</div>
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={openValuationModal} className="btn btn-outline-primary btn-sm">
                  Add Valuation
                </button>
                <button onClick={() => setShowEditValuationsModal(true)} className="btn btn-outline-secondary btn-sm">
                  Edit Valuations
                </button>
              </div>
            </div>
            {valuationSeries.book.length === 0 && valuationSeries.market.length === 0 ? (
              <div style={{ color: '#9CA3AF' }}>No valuation data available.</div>
            ) : (
              <Plot
                data={[
                  {
                    x: valuationSeries.book.map(p => p.x),
                    y: valuationSeries.book.map(p => p.y),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: 'Book Value',
                    line: { color: '#60a5fa' },
                    marker: { color: '#60a5fa' }
                  },
                  {
                    x: valuationSeries.market.map(p => p.x),
                    y: valuationSeries.market.map(p => p.y),
                    type: 'scatter',
                    mode: 'lines+markers',
                    name: 'Market Value',
                    line: { color: '#34d399' },
                    marker: { color: '#34d399' }
                  }
                ]}
                layout={{
                  xaxis: { title: 'Date' },
                  yaxis: { title: 'Value ($)', tickformat: '$,.2f' },
                  template: 'plotly_dark',
                  plot_bgcolor: '#0b1220',
                  paper_bgcolor: '#0b1220',
                  autosize: true,
                  margin: { l: 60, r: 10, b: 40, t: 10 }
                }}
                useResizeHandler={true}
                style={{ width: '100%', height: '340px' }}
              />
            )}
          </div>
        </div>
      )}

      {/* Tesla Connection Section */}
      {isEV && !vehicleInfo?.api_connected && (
        <div style={{
          backgroundColor: '#1f2937',
          border: '1px solid #374151',
          color: '#e5e7eb',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <h4>Connect to Tesla</h4>
          <p>Connect this vehicle to your Tesla account to automatically sync vehicle data.</p>

          <div style={{ marginBottom: '15px' }}>
            <button
              onClick={handleAuthorization}
              disabled={loading}
              style={{
                marginRight: '10px',
                backgroundColor: '#28a745',
                color: 'white',
                padding: '10px 15px',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {vehicles.length > 0 ? 'Refresh Tesla Connection' : 'Authorize Tesla Access'}
            </button>
          </div>

          {/* Tesla Vehicle Selection */}
          {vehicles.length > 0 && (
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                Select Tesla Vehicle to Link:
              </label>
              <select
                value={selectedTeslaVehicleId || ''}
                onChange={(e) => setSelectedTeslaVehicleId(e.target.value)}
                style={{ padding: '8px', marginRight: '10px', borderRadius: '4px', minWidth: '300px' }}
              >
                <option value="">Select a Tesla vehicle...</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                    {vehicle.display_name} (VIN: {vehicle.vin?.slice(-4) || 'Unknown'})
                  </option>
                ))}
              </select>

              <button
                onClick={linkTeslaVehicle}
                disabled={loading || !selectedTeslaVehicleId}
                style={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  padding: '8px 15px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading || !selectedTeslaVehicleId ? 'not-allowed' : 'pointer'
                }}
              >
                Link Vehicle
              </button>
            </div>
          )}
        </div>
      )}


      {/* Status Info */}
      {lastSync && (
        <div style={{
          backgroundColor: '#064e3b',
          border: '1px solid #065f46',
          color: '#d1fae5',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          Last sync: {lastSync}
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={{
          backgroundColor: '#7f1d1d',
          border: '1px solid #991b1b',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px',
          color: '#fee2e2'
        }}>
          {error}
        </div>
      )}

      {/* Manual Vehicle Data Entry (Non-API / ICE) */}
      {(!vehicleInfo?.api_connected) && (
        <div style={{
          backgroundColor: '#1f2937',
          border: '1px solid #374151',
          color: '#e5e7eb',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          <h3>Manual Vehicle Data Entry</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
            <div>
              <label style={{ color: '#9CA3AF' }}>Date</label>
              <input type="date" value={manualDate} onChange={e=>setManualDate(e.target.value)} style={{ width: '100%', padding: '6px', backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }}/>
            </div>
            <div>
              <label style={{ color: '#9CA3AF' }}>Odometer (mi)</label>
              <input type="number" value={manualOdometer} onChange={e=>setManualOdometer(e.target.value)} style={{ width: '100%', padding: '6px', backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }}/>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ color: '#9CA3AF', marginBottom: '6px' }}>Tire PSI</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))', gap: '10px' }}>
                <div>
                  <label style={{ color: '#9CA3AF' }}>FL</label>
                  <input type="number" step="0.1" value={manualFL} onChange={e=>setManualFL(e.target.value)} style={{ width: '100%', padding: '6px', backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }}/>
                </div>
                <div>
                  <label style={{ color: '#9CA3AF' }}>FR</label>
                  <input type="number" step="0.1" value={manualFR} onChange={e=>setManualFR(e.target.value)} style={{ width: '100%', padding: '6px', backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }}/>
                </div>
                <div>
                  <label style={{ color: '#9CA3AF' }}>RL</label>
                  <input type="number" step="0.1" value={manualRL} onChange={e=>setManualRL(e.target.value)} style={{ width: '100%', padding: '6px', backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }}/>
                </div>
                <div>
                  <label style={{ color: '#9CA3AF' }}>RR</label>
                  <input type="number" step="0.1" value={manualRR} onChange={e=>setManualRR(e.target.value)} style={{ width: '100%', padding: '6px', backgroundColor: '#111827', border: '1px solid #374151', color: '#e5e7eb' }}/>
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: '10px' }}>
            <button onClick={submitManualVehicleData} disabled={manualSaving} style={{
              backgroundColor: '#0d6efd', color: 'white', padding: '8px 15px', border: 'none', borderRadius: '4px', cursor: manualSaving ? 'not-allowed' : 'pointer'
            }}>{manualSaving ? 'Saving...' : 'Save Entry'}</button>
            {manualMessage && <span style={{ marginLeft: '10px', color: manualMessage==='Saved!' ? '#34d399' : '#f87171' }}>{manualMessage}</span>}
          </div>
        </div>
      )}

      {/* Vehicle Data Table (only for Mileage or Tire Pressure views) */}
      {(activeGraph === 'mileage' || activeGraph === 'tire_pressure') && vehicleData.length > 0 && (
        <div>
          <h3>Vehicle Data History</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '1px solid #374151',
              backgroundColor: '#111827',
              color: '#e5e7eb'
            }}>
              <thead>
                <tr style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Date/Time</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Odometer</th>
                  {isEV && (
                    <>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Battery</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Charge State</th>
                    </>
                  )}
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Tire Pressure (PSI)</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Location</th>
                  <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Source</th>
                </tr>
              </thead>
              <tbody>
                {vehicleData.filter(r => withinRange(r.recorded_at)).map((record) => (
                  <tr key={record.id}>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {new Date(record.recorded_at).toLocaleDateString()}
                      <br />
                      <small style={{ color: '#9CA3AF' }}>
                        {new Date(record.recorded_at).toLocaleTimeString()}
                      </small>
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {record.odometer_miles ? `${record.odometer_miles.toLocaleString()} mi` : 'No data'}
                    </td>
                    {isEV && (
                      <>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                          {formatBatteryInfo(record)}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                          {record.charge_state || 'No data'}
                        </td>
                      </>
                    )}
                    <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: '12px' }}>
                      {formatTirePressure(record)}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      {record.location ? `${record.location.lat?.toFixed(4)}, ${record.location.lng?.toFixed(4)}` : 'No data'}
                    </td>
                    <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                      <span style={{
                        backgroundColor: record.data_source.includes('api') ? '#065f46' : '#7c2d12',
                        color: '#e5e7eb',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '11px'
                      }}>
                        {record.data_source}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '10px', fontSize: '14px', color: '#9CA3AF' }}>
            Showing {vehicleData.filter(r => withinRange(r.recorded_at)).length} records in selected range
          </div>
        </div>
      )}

      {/* Maintenance Records Table */}
      <div style={{ marginTop: '40px' }}>
        <h3>Maintenance History</h3>

        {maintenanceRecords.length > 0 ? (
          <div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #374151',
                backgroundColor: '#111827',
                color: '#e5e7eb'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Service Type</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Odometer</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Service Provider</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Labor</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Parts</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Tax</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Other Fees</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Total</th>
                    <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {maintenanceRecords.map((record) => (
                    <tr key={record.id}>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {new Date(record.performed_date).toLocaleDateString()}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <div>
                          <strong>{record.maintenance_type.name}</strong>
                          {record.maintenance_type.category && (
                            <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                              {record.maintenance_type.category}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        {record.odometer_miles ? `${record.odometer_miles.toLocaleString()} mi` : 'N/A'}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <div>
                          {record.service_provider || 'N/A'}
                          {record.location && (
                            <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                              {record.location}
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                        {formatCurrency(record.labor_cost)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                        {formatCurrency(record.parts_cost)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                        {formatCurrency(record.tax_amount)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                        {formatCurrency(record.other_fees)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', fontWeight: 'bold' }}>
                        {formatCurrency(record.total_cost)}
                      </td>
                      <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                        <span style={{
                          backgroundColor: record.data_source === 'manual' ? '#7c2d12' : '#065f46',
                          color: '#e5e7eb',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          fontSize: '11px'
                        }}>
                          {record.data_source}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
                {/* Totals Footer */}
                <tfoot>
                  <tr style={{ backgroundColor: '#1f2937', color: '#e5e7eb', fontWeight: 'bold' }}>
                    <td colSpan="4" style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                      Totals ({maintenanceTotals.totalRecords} records):
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                      {formatCurrency(maintenanceTotals.totalLaborCost)}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                      {formatCurrency(maintenanceTotals.totalPartsCost)}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                      {formatCurrency(maintenanceTotals.totalTaxAmount)}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>
                      {formatCurrency(maintenanceTotals.totalOtherFees)}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right', fontSize: '16px' }}>
                      {formatCurrency(maintenanceTotals.totalCost)}
                    </td>
                    <td style={{ padding: '12px', border: '1px solid #ddd' }}></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div style={{ marginTop: '10px', fontSize: '14px', color: '#9CA3AF' }}>
              Total maintenance cost: <strong>{formatCurrency(maintenanceTotals.totalCost)}</strong>
              ({maintenanceTotals.totalRecords} records)
            </div>
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#1f2937',
            borderRadius: '4px',
            border: '1px solid #374151',
            color: '#e5e7eb'
          }}>
            <h4>No maintenance records yet</h4>
            <p>Maintenance records will appear here once you start adding them to your vehicle.</p>
            <div style={{ marginTop: '15px', fontSize: '14px', color: '#9CA3AF' }}>
              <strong>Future features:</strong>
              <ul style={{ listStyle: 'none', padding: 0, marginTop: '8px' }}>
                <li>• Manual maintenance entry</li>
                <li>• PDF invoice upload and parsing</li>
                <li>• Service reminder notifications</li>
                <li>• Cost analysis and trends</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Charging History Table */}
      {activeGraph === 'charging' && isEV && (
      <div style={{ marginTop: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Charging History</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={() => syncChargingHistory(false)}
              disabled={loading}
              className="btn btn-sm btn-outline-primary"
            >
              {loading ? 'Syncing...' : 'Sync Recent'}
            </button>
            <button
              onClick={() => syncChargingHistory(true)}
              disabled={loading}
              className="btn btn-sm btn-outline-secondary"
              title="Fetch all history from Tesla (up to 3 years)"
            >
              {loading ? 'Syncing...' : 'Full Sync'}
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        {chargingStats && (
          <div style={{
            backgroundColor: '#1f2937',
            padding: '15px',
            borderRadius: '4px',
            marginBottom: '20px',
            border: '1px solid #374151',
            color: '#e5e7eb'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
              <div>
                <strong>Total Sessions:</strong> {chargingStats.total_sessions}
              </div>
              <div>
                <strong>Total Cost:</strong> {formatCurrency(chargingStats.total_lifetime_cost)}
              </div>
              <div>
                <strong>Total Energy:</strong> {chargingStats.total_lifetime_energy_kwh?.toFixed(1)} kWh
              </div>
              <div>
                <strong>Avg Cost/kWh:</strong> {chargingStats.avg_cost_per_kwh ? `$${chargingStats.avg_cost_per_kwh.toFixed(3)}` : 'N/A'}
              </div>
            </div>
          </div>
        )}

        {chargingSessions.length > 0 ? (
          (() => {
            // Filter sessions by chart-selected date range (no rollups)
            const inRange = (s) => {
              const when = s.start_time || s.startTime || s.chargeStartDateTime;
              if (!when) return false;
              const dt = new Date(when);
              if (isNaN(dt)) return false;
              if (!chargeFrom || !chargeTo) return true;
              const start = new Date(chargeFrom + 'T00:00:00');
              const end = new Date(chargeTo + 'T23:59:59');
              return dt >= start && dt <= end;
            };
            const rows = (chargingSessions || []).filter(inRange);
            return rows.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  border: '1px solid #374151',
                  backgroundColor: '#111827',
                  color: '#e5e7eb'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: '#1f2937', color: '#e5e7eb' }}>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Date</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Location</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'center' }}>Duration</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Energy</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Miles Added</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Cost</th>
                      <th style={{ padding: '12px', border: '1px solid #ddd', textAlign: 'right' }}>Efficiency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((session) => (
                      <tr key={session.id}>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                          {new Date(session.start_time || session.startTime || session.chargeStartDateTime).toLocaleDateString()}
                          <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                            {new Date(session.start_time || session.startTime || session.chargeStartDateTime).toLocaleTimeString()}
                          </div>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                          <div style={{ fontWeight: 'bold' }}>
                            {session.location_name || session.siteLocationName || 'Unknown Location'}
                          </div>
                          {session.address && (
                            <div style={{ fontSize: '12px', color: '#9CA3AF' }}>
                              {session.address}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                          <span style={{
                            backgroundColor: (/supercharger/i.test(session.charge_port_type||'')) ? '#ff4444' :
                                           (/home|wall\s*connector/i.test(session.charge_port_type||'')) ? '#2196F3' : '#2196F3',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            fontSize: '11px'
                          }}>
                            {session.charge_port_type || 'Unknown'}
                          </span>
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center' }}>
                          {session.duration_minutes ? `${Math.round(session.duration_minutes)} min` : 'N/A'}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                          {session.energy_added_kwh ? `${Number(session.energy_added_kwh).toFixed(1)} kWh` : 'N/A'}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                          {session.miles_added ? `${Number(session.miles_added).toFixed(1)} mi` : 'N/A'}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                          {(() => {
                            const isPublic = (/super/i.test(session.charge_port_type || '')) || ((session.data_source || '').startsWith('tesla_api_public'));
                            const cost = (session.estimated_cost ?? session.total_cost);
                            const hasCost = cost !== null && cost !== undefined;
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <div>
                                  {hasCost ? formatCurrency(cost) : 'N/A'}
                                </div>
                                <div style={{ fontSize: '11px', color: '#9CA3AF' }}>
                                  {isPublic && session.total_cost != null
                                    ? 'Tesla reported cost'
                                    : (session.estimated_cost != null ? 'Estimated from utility rate × kWh' : '')}
                                </div>
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right' }}>
                          {session.efficiency_miles_per_kwh ? `${session.efficiency_miles_per_kwh} mi/kWh` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '40px',
                backgroundColor: '#1f2937',
                borderRadius: '4px',
                border: '1px solid #374151',
                color: '#e5e7eb'
              }}>
                <h4>No charging sessions in selected range</h4>
              </div>
            );
          })()
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#1f2937',
            borderRadius: '4px',
            border: '1px solid #374151',
            color: '#e5e7eb'
          }}>
            <h4>No charging sessions yet</h4>
            <p>Charging history will appear here once you sync data from Tesla or add sessions manually.</p>
            {vehicleInfo?.api_connected && (
              <button
                onClick={syncChargingHistory}
                style={{
                  marginTop: '15px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Sync Charging History from Tesla
              </button>
            )}
          </div>
        )}
      </div>
      )}

      {/* No Data Messages for Vehicle Data */}
      {isEV && !vehicleInfo?.api_connected && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#1f2937',
          borderRadius: '4px',
          border: '1px solid #374151',
          color: '#e5e7eb',
          marginTop: '20px'
        }}>
        <h3>Vehicle not connected to Tesla API</h3>
        <p>Connect this vehicle to Tesla to start automatically tracking vehicle data.</p>
      </div>
      )}

      {vehicleInfo?.api_connected && vehicleData.length === 0 && !loading && (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: '#1f2937',
          borderRadius: '4px',
          border: '1px solid #374151',
          color: '#e5e7eb',
          marginTop: '20px'
        }}>
          <h3>No vehicle data yet</h3>
          <p>Click "Sync Vehicle Data" to fetch and store your vehicle's current data from Tesla.</p>
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div style={{
          textAlign: 'center',
          padding: '20px',
          fontSize: '16px',
          color: '#007bff'
        }}>
          Loading...
        </div>
      )}

      {showUtilityModal && utilityRow && (
        <AssetExpenseLinkModal
          visible={showUtilityModal}
          onClose={()=> { setShowUtilityModal(false); setUtilityRow(null); }}
          sourceType={utilityRow.sourceType}
          transaction={utilityRow}
          domain={getDomainForCategory(utilityRow.expense_category)}
          initialValues={{
            asset_id: utilityRow.linked_asset_id || '',
            expense_category: utilityRow.expense_category || '',
            linked_asset_expense_id: utilityRow.linked_asset_expense_id || '',
            property_id: utilityRow.linked_property_id || '',
            recurring_series_id: utilityRow.linked_recurring_series_id || '',
            utility_bill_id: utilityRow.linked_utility_bill_id || '',
          }}
          onSaved={async (payload)=>{
            const ae = payload || {};
            setShowUtilityModal(false);
            setUtilityRow(null);
            loadMaintenanceRecords(); // Full refresh
          }}
          onUnlinked={async ()=>{
            setShowUtilityModal(false);
            setUtilityRow(null);
            loadMaintenanceRecords(); // Full refresh
          }}
        />
      )}

      {showDebtModal && linkingRow && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
          <div style={{ width: '720px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:18, fontWeight:700 }}>Link Payment to Debt</div>
              <button onClick={()=>{ setShowDebtModal(false); setLinkingRow(null); }} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
            </div>
            <div style={{ padding:16 }}>
              {modalError && <div className="alert alert-danger">{modalError}</div>}
              {modalPhase === 'chooseDebt' ? (
                <>
                  <div style={{ marginBottom:12 }}>Select the loan/mortgage this payment belongs to:</div>
                  <div style={{ maxHeight:'40vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                    <table style={{ width:'100%' }}>
                      <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                        <tr>
                          <th style={{ textAlign:'left', padding:8 }}>Debt Name</th>
                          <th style={{ textAlign:'left', padding:8 }}>Lender</th>
                          <th style={{ textAlign:'right', padding:8 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debtList.map(d => (
                          <tr key={d.id} style={{ borderBottom:'1px solid #1f2937' }}>
                            <td style={{ padding:8 }}>{d.debt_type.replace('_',' ').toUpperCase()}</td>
                            <td style={{ padding:8 }}>{d.lender_name || 'N/A'}</td>
                            <td style={{ padding:8, textAlign:'right' }}>
                              <button className="btn btn-sm btn-primary" onClick={() => { setSelectedDebt(d); loadDebtCandidates(d); }}>Select</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom:12 }}>
                    Matching {selectedDebt.debt_type.replace('_',' ')} candidates for <strong>{linkingRow.description}</strong> ({formatCurrency(linkingRow.amount)}):
                  </div>
                  <div style={{ maxHeight:'40vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                    <table style={{ width:'100%' }}>
                      <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                        <tr>
                          <th style={{ textAlign:'left', padding:8 }}>Date</th>
                          <th style={{ textAlign:'right', padding:8 }}>Amount</th>
                          <th style={{ textAlign:'right', padding:8 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debtCandidates.map(c => (
                          <tr key={c.id} style={{ borderBottom:'1px solid #1f2937' }}>
                            <td style={{ padding:8 }}>{c.payment_date}</td>
                            <td style={{ padding:8, textAlign:'right' }}>{formatCurrency(c.total_amount)}</td>
                            <td style={{ padding:8, textAlign:'right' }}>
                              <button className="btn btn-sm btn-success" onClick={() => confirmLinkToCandidate(c)}>Link</button>
                            </td>
                          </tr>
                        ))}
                        {debtCandidates.length === 0 && (
                          <tr><td colSpan={3} style={{ padding:12, textAlign:'center', color:'#9ca3af' }}>No matching payments found in the amortization schedule.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <button className="btn btn-link btn-sm mt-3" style={{ color:'#93c5fd' }} onClick={() => setModalPhase('chooseDebt')}>← Back to debt list</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Inline Expenses Transactions Table component (mirrors Income Statement table formatting/functionality)
function ExpensesTransactionsTable({ assetId, fromDate, toDate, onExpenseChange, assetMaps, assetName, allAssets, vehicleInfo, vehicleId, onSelectRecurring }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const ExcelIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
      <polyline points="14 2 14 8 20 8"></polyline>
      <line x1="16" y1="13" x2="8" y2="13"></line>
      <line x1="16" y1="17" x2="8" y2="17"></line>
      <polyline points="10 9 9 9 8 9"></polyline>
    </svg>
  );

  const processedTransactions = useMemo(() => {
    return rows.map(t => {
      const asset = t.linked_asset_id ? (allAssets || []).find(a => String(a.id) === String(t.linked_asset_id)) : null;
      const assetLabel = asset ? buildAssetLabel(asset, { ...assetMaps, transactionDomain: getDomainForCategory(t.expense_category) }) : '';
      
      let debtLabel = '';
      const ld = t.linked_debt_info || t.linkedDebtInfo || t.linkedDebt;
      if (ld && (ld.type === 'auto_loan' || ld.type === 'mortgage') && (t.linked_debt_id || t.linkedDebtId)) {
        debtLabel = ld.debt_label || ld.debt_name || 'Debt';
      }

      return {
        ...t,
        expense_group: t.expense_group || '',
        linked_asset_label: assetLabel,
        linked_debt_label: debtLabel
      };
    });
  }, [rows, allAssets, assetMaps]);

  const handleExport = () => {
    const columns = [
      { header: 'Date', key: t => t.date ? new Date(t.date).toLocaleDateString() : '' },
      { header: 'Type', key: 'source_type' },
      { header: 'Group', key: 'expense_group' },
      { header: 'Description', key: 'description' },
      { header: 'User Description', key: 'user_description' },
      { header: 'Amount', key: 'amount' },
      { header: 'Category', key: 'expense_category' },
      { header: 'Asset Link', key: t => t.linked_asset_label || '' },
      { header: 'Debt Link', key: t => t.linked_debt_label || '' }
    ];
    
    const nameForFile = assetName || (vehicleInfo ? (vehicleInfo.year + ' ' + vehicleInfo.make + ' ' + vehicleInfo.model) : null) || vehicleId || 'Vehicle';
    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_');
    const filename = `Vehicle_Expenses_${nameForFile}_${dateStr}`;
    exportToExcel(processedTransactions, filename, columns);
  };
  const [error, setError] = React.useState('');
  const [sortField, setSortField] = React.useState('date');
  const [sortDir, setSortDir] = React.useState('desc');
  const [showLinkModal, setShowLinkModal] = React.useState(false);
  const [editRow, setEditRow] = React.useState(null);
  const [editSubCategory, setEditSubCategory] = React.useState('');
  const [editRecurring, setEditRecurring] = React.useState(false);
  const [editFrequency, setEditFrequency] = React.useState('');
  const [savingEdit, setSavingEdit] = React.useState(false);

  // Missing state for dropdowns and linking modals
  const [expenseCategories, setExpenseCategories] = React.useState({ items: [], noneItem: { value: 'none', label: 'None' } });
  const [openDropdownId, setOpenDropdownId] = React.useState(null);
  const [expandedParents, setExpandedParents] = React.useState({});

  const [showUtilityModal, setShowUtilityModal] = React.useState(false);
  const [utilityRow, setUtilityRow] = React.useState(null);

  const [showDebtModal, setShowDebtModal] = React.useState(false);
  const [linkingRow, setLinkingRow] = React.useState(null);
  const [debtList, setDebtList] = React.useState([]);
  const [selectedDebt, setSelectedDebt] = React.useState(null);
  const [debtCandidates, setDebtCandidates] = React.useState([]);
  const [modalLoading, setModalLoading] = React.useState(false);
  const [modalError, setModalError] = React.useState('');
  const [modalPhase, setModalPhase] = React.useState('chooseDebt');

  // Load categories
  React.useEffect(() => {
    const loadCats = async () => {
      try {
        const res = await axios.get('/api/balance_sheet/financing/revolvers/expense-category-choices/');
        setExpenseCategories(buildCategoryTreeSorted(res.data));
      } catch (e) {
        setExpenseCategories({ items: [], noneItem: { value: 'none', label: 'None' } });
      }
    };
    loadCats();
  }, []);

  const buildAssetUrl = (asset) => {
    if (!asset) return null;
    const id = asset.id;
    const type2 = (asset.asset_type_2 || asset.type_2 || asset.type2 || '').toLowerCase();
    if (type2 === 'vehicle') return `/balance_sheet/assets/physical_assets/vehicles/${id}`;
    if (type2 === 'resi_property') return `/balance_sheet/assets/physical_assets/resi_properties/${id}`;
    if (type2 === 'structure' || type2 === 'land') {
      const rp = asset.residential_property || asset.residentialProperty || asset.parent_property || asset.parent;
      const rpId = (rp && typeof rp === 'object') ? (rp.id || rp.pk) : rp;
      if (rpId) return `/balance_sheet/assets/physical_assets/resi_properties/${rpId}`;
    }
    return null;
  };

  const updateExpenseCategory = async (row, category) => {
    // Handle synthetic IDs (e.g., synth-123-456) for split child transactions
    let realId = row.id;
    if (typeof row.id === 'string' && row.id.startsWith('synth-')) {
      const parts = row.id.split('-');
      if (parts.length >= 3) {
        realId = parts[parts.length - 1];
        console.log(`Resolved synthetic ID ${row.id} to real ID ${realId}`);
      }
    }

    try {
      const url = row.source_type === 'revolver'
        ? `/api/balance_sheet/financing/revolvers/transactions/${realId}/`
        : `/api/balance_sheet/assets/cash_accounts/transactions/${realId}/`;
      
      await axios.patch(url, { expense_category: category });
      
      // Update local state
      setRows(prev => prev.map(t => (t.id === row.id && t.source_type === row.source_type) ? { ...t, expense_category: category, txn_category: category } : t));
      setOpenDropdownId(null);

      // standard triggers
      const domain = getDomainForCategory(category);
      const isWaste = String(category).startsWith('waste_') || String(category).includes('sanitation');
      if (domain !== 'generic' || isWaste) {
        openUtilitiesModal({ ...row, expense_category: category });
      } else if (category === 'payments') {
        openDebtModalForRow({ ...row, expense_category: category });
      }
      
      if (onExpenseChange) onExpenseChange();
    } catch (e) {
      console.error('Failed to update category', e);
      alert('Failed to update category.');
    }
  };

  const openUtilitiesModal = (row) => {
    setUtilityRow({
      ...row,
      sourceType: row.source_type || 'cash',
      expense_category: row.expense_category || row.txn_category || ''
    });
    setShowUtilityModal(true);
  };

  const openDebtModalForRow = async (row) => {
    setModalError('');
    setModalLoading(true);
    setShowDebtModal(true);
    setLinkingRow(row);
    setSelectedDebt(null);
    setDebtCandidates([]);
    setModalPhase('chooseDebt');
    try {
      const res = await axios.get('/api/balance_sheet/financing/');
      const allDebts = Array.isArray(res.data) ? res.data : [];
      const nonRevolving = allDebts.filter(d => (d.debt_type === 'auto_loan' || d.debt_type === 'mortgage'));
      setDebtList(nonRevolving);
    } catch (e) {
      setModalError('Failed to load debts.');
    } finally {
      setModalLoading(false);
    }
  };

  const loadDebtCandidates = async (debt) => {
    if (!linkingRow || !debt) return;
    setModalError('');
    setModalLoading(true);
    try {
      const url = `/api/balance_sheet/assets/cash_accounts/transactions/${linkingRow.id}/candidate-debt-payments`;
      const params = { debt_type: debt.debt_type, debt_id: debt.id };
      const res = await axios.get(url, { params });
      setDebtCandidates(res.data?.candidates || []);
      setModalPhase('chooseCandidate');
    } catch (e) {
      setModalError('Failed to load candidates.');
    } finally {
      setModalLoading(false);
    }
  };

  const confirmLinkToCandidate = async (candidate) => {
    if (!linkingRow || !selectedDebt || !candidate) return;
    setModalError('');
    setModalLoading(true);
    try {
      const url = `/api/balance_sheet/assets/cash_accounts/transactions/${linkingRow.id}/link-debt-payment/`;
      await axios.post(url, {
        debt_type: selectedDebt.debt_type,
        debt_id: selectedDebt.id,
        candidate_id: candidate.id,
      });
      setShowDebtModal(false);
      if (onExpenseChange) onExpenseChange();
    } catch (e) {
      setModalError('Failed to link payment.');
    } finally {
      setModalLoading(false);
    }
  };

  const setModalPhaseSafe = (phase) => setModalPhase(phase);

  React.useEffect(() => {
    const fetchTx = async () => {
      if (!assetId || !fromDate || !toDate) { setRows([]); return; }
      setLoading(true); setError('');
      try {
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${assetId}/linked-expenses/`, {
          params: {
            start_date: fromDate,
            end_date: toDate,
            only_txn_backed: 1,
            // Include EV charging AssetExpense rows (asset_input) even if not backed by a transaction
            include_asset_input: 1,
          }
        });
        const actual = Array.isArray(res.data?.actual) ? res.data.actual : [];
        setRows(actual);
      } catch (e) {
        console.error('Failed to load transactions', e);
        setError('Failed to load transactions');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTx();
  }, [assetId, fromDate, toDate]);

  const sorted = React.useMemo(() => {
    const clone = [...rows];
    const dir = sortDir === 'asc' ? 1 : -1;
    clone.sort((a,b) => {
      const ad = new Date(a.date || a.transaction_date || a.post_date).getTime();
      const bd = new Date(b.date || b.transaction_date || b.post_date).getTime();
      if (sortField === 'date') return (ad - bd) * dir;
      if (sortField === 'group') return String(a.expense_group||'').localeCompare(String(b.expense_group||'')) * dir;
      if (sortField === 'description') return String(a.description||'').localeCompare(String(b.description||'')) * dir;
      if (sortField === 'user_description') return String(a.user_description||'').localeCompare(String(b.user_description||'')) * dir;
      if (sortField === 'category') return String(a.txn_category||a.expense_category||'').localeCompare(String(b.txn_category||b.expense_category||'')) * dir;
      if (sortField === 'amount') return ((Number(a.amount)||0)-(Number(b.amount)||0)) * dir;
      return 0;
    });
    return clone;
  }, [rows, sortField, sortDir]);

  const fmtCurrency = (v) => {
    const n = Number(v||0);
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  };

  const headerCell = (label, field, style={}) => (
    <th style={{ cursor:'pointer', ...style }} onClick={()=>{
      if (sortField === field) setSortDir(sortDir==='asc'?'desc':'asc'); else { setSortField(field); setSortDir('desc'); }
    }}>
      {label} {sortField===field ? (sortDir==='asc'?'▲':'▼') : ''}
    </th>
  );

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display:'flex', alignItems:'center', marginBottom:8 }}>
        <div style={{ fontWeight:600, color:'#e5e7eb' }}>Transactions in view</div>
        <button 
          onClick={handleExport} 
          className="btn btn-outline-success btn-sm"
          style={{ marginLeft: 12 }}
          title="Export to Excel"
        >
          <ExcelIcon /> Export
        </button>
        <div style={{ marginLeft:'auto', fontSize:12, color:'#6b7280' }}>{fromDate && toDate ? `${fromDate} to ${toDate}` : ''}</div>
      </div>
      {loading ? (
        <div style={{ color:'#9CA3AF' }}>Loading transactions…</div>
      ) : error ? (
        <div className="alert alert-warning" style={{ background:'transparent', color:'#fbbf24' }}>{error}</div>
      ) : (
        <div style={{ background:'#0b1220', border:'1px solid #374151', borderRadius:8, overflow:'hidden' }}>
          <table className="table" style={{ margin:0, color:'#e5e7eb', background:'#0b1220', '--bs-table-bg':'#0b1220', '--bs-table-color':'#e5e7eb', '--bs-table-striped-bg':'#0f172a', '--bs-table-accent-bg':'transparent' }}>
            <thead style={{ background:'#111827', color:'#e5e7eb' }}>
              <tr>
                {headerCell('Date','date')}
                {headerCell('Group','group')}
                {headerCell('Description','description')}
                {headerCell('User Description','user_description')}
                {headerCell('Category','category', { minWidth:'160px' })}
                <th>Asset Link</th>
                <th>Linked To-Do</th>
                <th>Recurring</th>
                <th>Debt Link</th>
                {headerCell('Amount','amount', { textAlign:'right' })}
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr><td colSpan={10} className="text-center text-muted py-4">No transactions found for the selected period.</td></tr>
              )}
              {sorted.map((t, idx) => (
                <tr key={idx}>
                  <td>{new Date(t.date || t.transaction_date || t.post_date).toLocaleDateString()}</td>
                  <td><ExpenseGroupPill group={t.expense_group} /></td>
                  <td>{t.description}</td>
                  <td>{t.user_description || ''}</td>
                  <td>
                    {/* Expense category badge dropdown */}
                    {(() => {
                      const category = t.txn_category || t.expense_category || 'none';
                      const isOpen = openDropdownId === t.id;
                      
                      const getLabelForVal = (val) => {
                        const v = String(val);
                        for (const it of expenseCategories.items) {
                          if (it.value === v) return it.label;
                          if (it.children) {
                            const ch = it.children.find(c => c.value === v);
                            if (ch) return ch.label;
                          }
                        }
                        return expenseCategories.noneItem?.label || 'None';
                      };
                      
                      const label = getLabelForVal(category);
                      
                      return (
                        <div className="position-relative" style={{ display: 'inline-block' }}>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setOpenDropdownId(prev => prev === t.id ? null : t.id); }}
                            style={{ borderRadius: '12px', fontSize: '0.75rem', padding: '4px 8px', cursor: 'pointer', whiteSpace:'nowrap' }}
                          >
                            {label} ▼
                          </button>
                          {isOpen && (
                            <div
                              className="position-absolute"
                              style={{ top: '100%', left: 0, zIndex: 1000, minWidth: '280px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.375rem', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.3)', marginTop: 2 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '4px 0' }}>
                                {expenseCategories.items.map((it) => (
                                  <div key={it.value}>
                                    <button
                                      className="w-100 text-start"
                                      style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                      onClick={() => {
                                        if (Array.isArray(it.children) && it.children.length > 0) {
                                          const dropdownId = t.id;
                                          setExpandedParents(prev => {
                                            const cur = new Set(prev[dropdownId] || []);
                                            if (cur.has(it.value)) cur.delete(it.value); else cur.add(it.value);
                                            return { ...prev, [dropdownId]: cur };
                                          });
                                        } else {
                                          updateExpenseCategory(t, String(it.value));
                                          setOpenDropdownId(null);
                                        }
                                      }}
                                    >
                                      {(Array.isArray(it.children) && it.children.length > 0) ? (expandedParents[t.id]?.has(it.value) ? '▼ ' : '► ') : ''}
                                      {it.label}
                                    </button>
                                    {(Array.isArray(it.children) && it.children.length > 0 && expandedParents[t.id]?.has(it.value)) && (
                                      <div>
                                        {it.children.map(ch => (
                                          <button
                                            key={ch.value}
                                            className="w-100 text-start"
                                            style={{ display: 'block', padding: '6px 16px 6px 28px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                            onClick={() => { updateExpenseCategory(t, String(ch.value)); setOpenDropdownId(null); }}
                                          >
                                            {ch.label}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                                <div style={{ borderTop: '1px solid #374151', margin: '4px 0' }} />
                                <button
                                  className="w-100 text-start"
                                  style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                                  onClick={() => { updateExpenseCategory(t, String(expenseCategories.noneItem.value)); setOpenDropdownId(null); }}
                                >
                                  {expenseCategories.noneItem.label || 'None'}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td>
                    <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                      {(() => {
                        const aid = t.linked_asset_id;
                        if (!aid) {
                          const cat = String(t.txn_category || t.expense_category || '');
                          const domain = getDomainForCategory(cat);
                          const isWaste = cat.startsWith('waste_') || cat.includes('sanitation');
                          const isLinkable = domain !== 'generic' || isWaste;
                          if (!isLinkable) return null;

                          let labelBtn = 'Link Asset';
                          if (domain === 'utilities') labelBtn = 'Link Utilities';
                          else if (domain === 'home') labelBtn = 'Link Home';
                          else if (domain === 'landscaping') labelBtn = 'Link Landscaping';
                          else if (domain === 'vehicle') labelBtn = 'Link Vehicle';
                          else if (isWaste) labelBtn = 'Link Waste';

                          return (
                            <button
                              className="btn btn-link btn-sm"
                              style={{ color:'#93c5fd', padding:0 }}
                              onClick={(e) => { e.preventDefault(); openUtilitiesModal(t); }}
                            >
                              {labelBtn}
                            </button>
                          );
                        }
                        const asset = assetMaps.byId.get(String(aid));
                        const url = buildAssetUrl(asset);
                        const cat = String(t.txn_category || t.expense_category || '');
                        const domain = getDomainForCategory(cat);
                        const label = buildAssetLabel(asset, { ...assetMaps, transactionDomain: domain });

                        return (
                          <>
                            {url ? (
                              <Link to={url} title={label} style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }}>{label}</Link>
                            ) : (
                              <span title={label} style={{ color:'#93c5fd' }}>{label}</span>
                            )}
                            <button
                              className="btn btn-link btn-sm"
                              style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                              onClick={(e) => { e.preventDefault(); openUtilitiesModal(t); }}
                            >
                              Edit Link
                            </button>
                          </>
                        );
                      })()}
                    </span>
                  </td>
                  <td>
                    <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                      {(() => {
                        const todoInfo = t.linked_todo_info;
                        if (!todoInfo) return null;

                        let url = null;
                        if (todoInfo.asset_type === 'vehicle') {
                          url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
                        } else if (todoInfo.property_id) {
                          url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
                        }

                        const statusColor = todoInfo.status === 'completed' ? '#22c55e' : (todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af');
                        
                        const content = (
                          <span 
                            style={{ color: '#93c5fd', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.85rem' }}
                            title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}
                          >
                            {todoInfo.title}
                          </span>
                        );

                        return (
                          <>
                            {url ? (
                              <Link to={url}>{content}</Link>
                            ) : (
                              content
                            )}
                            <span style={{ fontSize: '10px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 4px', marginLeft: '4px' }}>
                              {todoInfo.status_display}
                            </span>
                          </>
                        );
                      })()}
                    </span>
                  </td>
                  <td>
                    {(() => {
                        const recName = t.recurring_name || (t.linked_expense_info && t.linked_expense_info.recurring_name);
                        const recFreq = t.recurring_frequency || (t.linked_expense_info && t.linked_expense_info.recurring_frequency);
                        const recType = t.recurring_type || (t.linked_expense_info && t.linked_expense_info.recurring_type);
                        if (!recName) return null;
                        return (
                            <button
                                className="btn btn-link btn-sm"
                                style={{ color:'#93c5fd', padding:0, textAlign:'left', textDecoration:'none', whiteSpace:'nowrap' }}
                                title={`Frequency: ${recFreq || 'N/A'}\nType: ${recType || 'N/A'}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onSelectRecurring(recName);
                                }}
                            >
                                {recName}
                            </button>
                        );
                    })()}
                  </td>
                  <td>
                    <span style={{ display:'inline-flex', gap:8, alignItems:'center' }}>
                      {t.linked_debt_info && (t.linked_debt_info.type === 'auto_loan' || t.linked_debt_info.type === 'mortgage') && t.linked_debt_id ? (
                        <>
                          <a
                            href={t.linked_debt_info.type === 'auto_loan' ? `/balance_sheet/financing/auto_loans/${t.linked_debt_id}` : `/balance_sheet/financing/mortgages/${t.linked_debt_id}`}
                            style={{ color:'#93c5fd', whiteSpace:'nowrap', background:'transparent', border:'none' }}
                            title={t.linked_debt_info.display_text || ''}
                          >
                            {t.linked_debt_info.debt_label || t.linked_debt_info.debt_name || '🔗 Debt'}
                          </a>
                          <button
                            className="btn btn-link btn-sm"
                            style={{ color:'#93c5fd', padding:0, marginLeft:8 }}
                            onClick={(e) => { e.preventDefault(); openDebtModalForRow(t); }}
                          >
                            Edit Link
                          </button>
                        </>
                      ) : (
                        String(t.txn_category || t.expense_category || '') === 'payments' && (
                          <button
                            className="btn btn-link btn-sm"
                            style={{ color:'#93c5fd', padding:0 }}
                            onClick={(e) => { e.preventDefault(); openDebtModalForRow(t); }}
                          >
                            Link Debt
                          </button>
                        )
                      )}
                    </span>
                  </td>
                  <td style={{ textAlign:'right' }}>{fmtCurrency(t.amount)}</td>
                  <td style={{ whiteSpace:'nowrap' }}>{t.source_display || 'Card'}</td>
                  <td>
                    <button className="btn btn-outline-danger btn-sm" style={{ padding:'2px 8px', fontSize:'0.75rem' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop:8, fontSize:12, color:'#9CA3AF' }}>Total rows: {rows.length}</div>

      {showUtilityModal && utilityRow && (
        <AssetExpenseLinkModal
          visible={showUtilityModal}
          onClose={()=> { setShowUtilityModal(false); setUtilityRow(null); }}
          sourceType={utilityRow.sourceType}
          transaction={utilityRow}
          domain={getDomainForCategory(utilityRow.expense_category)}
          initialValues={{
            asset_id: utilityRow.linked_asset_id || '',
            expense_category: utilityRow.expense_category || '',
            linked_asset_expense_id: utilityRow.linked_asset_expense_id || '',
            property_id: utilityRow.linked_property_id || '',
            recurring_series_id: utilityRow.linked_recurring_series_id || '',
            utility_bill_id: utilityRow.linked_utility_bill_id || '',
          }}
          onSaved={async (payload)=>{
            const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${assetId}/linked-expenses/`, {
              params: { start_date: fromDate, end_date: toDate, only_txn_backed: 1, include_asset_input: 1 }
            });
            setRows(Array.isArray(res.data?.actual) ? res.data.actual : []);
            setShowUtilityModal(false);
            setUtilityRow(null);
            if (onExpenseChange) onExpenseChange();
          }}
          onUnlinked={async ()=>{
            const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${assetId}/linked-expenses/`, {
              params: { start_date: fromDate, end_date: toDate, only_txn_backed: 1, include_asset_input: 1 }
            });
            setRows(Array.isArray(res.data?.actual) ? res.data.actual : []);
            setShowUtilityModal(false);
            setUtilityRow(null);
            if (onExpenseChange) onExpenseChange();
          }}
        />
      )}

      {showDebtModal && linkingRow && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}>
          <div style={{ width: '720px', maxWidth: '95vw', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb' }}>
            <div style={{ padding:'12px 16px', borderBottom:'1px solid #374151', display:'flex', alignItems:'center' }}>
              <div style={{ fontSize:18, fontWeight:700 }}>Link Payment to Debt</div>
              <button onClick={()=>{ setShowDebtModal(false); setLinkingRow(null); }} style={{ marginLeft:'auto' }} className="btn btn-sm btn-secondary">✕</button>
            </div>
            <div style={{ padding:16 }}>
              {modalError && <div className="alert alert-danger">{modalError}</div>}
              {modalPhase === 'chooseDebt' ? (
                <>
                  <div style={{ marginBottom:12 }}>Select the loan/mortgage this payment belongs to:</div>
                  <div style={{ maxHeight:'40vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                    <table style={{ width:'100%' }}>
                      <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                        <tr>
                          <th style={{ textAlign:'left', padding:8 }}>Debt Name</th>
                          <th style={{ textAlign:'left', padding:8 }}>Lender</th>
                          <th style={{ textAlign:'right', padding:8 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debtList.map(d => (
                          <tr key={d.id} style={{ borderBottom:'1px solid #1f2937' }}>
                            <td style={{ padding:8 }}>{d.debt_type.replace('_',' ').toUpperCase()}</td>
                            <td style={{ padding:8 }}>{d.lender_name || 'N/A'}</td>
                            <td style={{ padding:8, textAlign:'right' }}>
                              <button className="btn btn-sm btn-primary" onClick={() => { setSelectedDebt(d); loadDebtCandidates(d); }}>Select</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom:12 }}>
                    Matching {selectedDebt.debt_type.replace('_',' ')} candidates for <strong>{linkingRow.description}</strong> ({fmtCurrency(linkingRow.amount)}):
                  </div>
                  <div style={{ maxHeight:'40vh', overflowY:'auto', border:'1px solid #374151', borderRadius:6 }}>
                    <table style={{ width:'100%' }}>
                      <thead style={{ position:'sticky', top:0, background:'#111827' }}>
                        <tr>
                          <th style={{ textAlign:'left', padding:8 }}>Date</th>
                          <th style={{ textAlign:'right', padding:8 }}>Amount</th>
                          <th style={{ textAlign:'right', padding:8 }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debtCandidates.map(c => (
                          <tr key={c.id} style={{ borderBottom:'1px solid #1f2937' }}>
                            <td style={{ padding:8 }}>{c.payment_date}</td>
                            <td style={{ padding:8, textAlign:'right' }}>{fmtCurrency(c.total_amount)}</td>
                            <td style={{ padding:8, textAlign:'right' }}>
                              <button className="btn btn-sm btn-success" onClick={() => confirmLinkToCandidate(c)}>Link</button>
                            </td>
                          </tr>
                        ))}
                        {debtCandidates.length === 0 && (
                          <tr><td colSpan={3} style={{ padding:12, textAlign:'center', color:'#9ca3af' }}>No matching payments found in the amortization schedule.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <button className="btn btn-link btn-sm mt-3" style={{ color:'#93c5fd' }} onClick={() => setModalPhase('chooseDebt')}>← Back to debt list</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VehicleDetail;